import CircleWithTickIcon from "@/assets/colorable/circle_with_tick.svg?react";
import CrownPlusIcon from "@/assets/colorable/crown_plus.svg?react";
import EyeIcon from "@/assets/colorable/eye.svg?react";
import EyeClosedIcon from "@/assets/colorable/eye_closed.svg?react";
import GlobeIcon from "@/assets/colorable/globe.svg?react";
import LockIcon from "@/assets/colorable/lock.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import ShieldIcon from "@/assets/colorable/shield.svg?react";
import UsersIcon from "@/assets/colorable/users.svg?react";
import AmbassadorBenefits from "@/components/Common/referral/AmbassadorBenefits";
import clsxm from "@/lib/clsxm";
import type {TReferralTab} from "@/store/types";
import type {FC, ReactNode} from "react";
import {useTranslation} from "react-i18next";

const AmbassadorProgramContent: FC<{
  agencyId?: number | null;
  cardsClassName?: string;
  onSelect(section: TReferralTab): void;
}> = ({agencyId, cardsClassName, onSelect}) => {
  const {t} = useTranslation("referral");

  const renderReferralCard = ({
    Icon,
    buttonText,
    description,
    features,
    onClick,
    title,
  }: {
    Icon: ReactNode;
    buttonText: string;
    description: string;
    features: {icon: ReactNode; text: string}[];
    onClick: () => void;
    title: string;
  }) => (
    <div
      onClick={onClick}
      className="group border-primary-light-solid hover:border-primary-medium-solid rounded-2.5xl flex flex-1 cursor-pointer flex-col gap-5 border p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex flex-col gap-3">
        <div className="bg-primary-powder flex size-10 shrink-0 items-center justify-center rounded-md">{Icon}</div>
        <span className="text-neutral-black text-xl leading-3.5 font-semibold">{title}</span>
        <span className="text-neutral-grey-dark text-xs">{description}</span>
      </div>
      <div className="flex flex-col gap-3">
        {features.map((feature, index) => (
          <div key={index} className="flex items-center gap-2">
            {feature.icon}
            <span className="text-neutral-black text-xs">{feature.text}</span>
          </div>
        ))}
      </div>
      <div className="flex h-15 items-center gap-3 p-2">
        <CrownPlusIcon className="text-primary-dark-solid size-3.5" />
        <span className="text-neutral-black text-sm leading-4 font-medium">{buttonText}</span>
        <RightArrowIcon className="text-primary-dark-solid size-3" />
      </div>
    </div>
  );

  return (
    <>
      <span className="text-neutral-grey-dark text-sm">{t("ambassador_program_description")}</span>
      <div className={clsxm("flex flex-col items-stretch gap-5 sm:flex-row", cardsClassName)}>
        {!agencyId &&
          renderReferralCard({
            Icon: <EyeIcon className="text-primary-medium-solid h-2.5 w-3.5" />,
            buttonText: t("public_referral_cta"),
            description: t("public_referral_description"),
            features: [
              {
                icon: <GlobeIcon className="text-neutral-grey-dark size-3 shrink-0" />,
                text: t("public_referral_feature_1"),
              },
              {
                icon: <ShieldIcon className="h-3 w-3.5 shrink-0 text-black" />,
                text: t("public_referral_feature_2"),
              },
              {
                icon: <CircleWithTickIcon className="text-success-herbal size-3.5 shrink-0" />,
                text: t("public_referral_feature_3"),
              },
            ],
            onClick: () => onSelect("public"),
            title: t("public_referral"),
          })}
        {renderReferralCard({
          Icon: <EyeClosedIcon className="text-primary-medium-solid h-2 w-3.5" />,
          buttonText: t("private_referral_cta"),
          description: t("private_referral_description"),
          features: [
            {icon: <LockIcon className="text-neutral-grey-dark size-3.5" />, text: t("private_referral_feature_1")},
            {
              icon: <CircleWithTickIcon className="text-success-herbal size-3.5" />,
              text: t("private_referral_feature_2"),
            },
            {icon: <UsersIcon className="text-primary-dark-solid size-3.5" />, text: t("private_referral_feature_3")},
          ],
          onClick: () => onSelect("private"),
          title: t("private_referral"),
        })}
      </div>
      <AmbassadorBenefits />
      <div className="bg-neutral-grey-light-dark h-px w-full" />
      <div className="flex flex-col gap-2">
        <span className="text-neutral-grey-dark text-xs">*{t("referral_guide_condition_one")}</span>
        <span className="text-neutral-grey-dark text-xs">*{t("referral_guide_condition_two")}</span>
      </div>
    </>
  );
};

export default AmbassadorProgramContent;
